// Wire schemas for /recipes/* endpoints.
//
// `recipeMeResponseSchema` is the SvelteKit FE's single source of truth
// for "what does the active org's recipe look like" — labels, severities,
// party roles, signature roles, areas, finding categories, asset
// statuses, plus the 5 primitives. The frontend's screens (cartera,
// firmar, revision, lista, p/[token]) read off this instead of
// hardcoding a vertical-specific mirror.
//
// Two server-side fields are deliberately omitted:
//   - aiPromptProfile / aiPrompts — vertical-specific AI prompt addenda
//     consumed by ai-svc when composing prompts. Pure server concern;
//     leaking the wording would also expose competitive product detail.
//   - pdfTemplate — string ID resolved by the PDF renderer service.
//     The FE never picks a template; it just reads pdfUrl off the
//     report row.
//
// Everything else passes through. Future fields added to the engine's
// Recipe shape become visible here automatically because we strip the
// two ignored keys via `omit()` rather than rebuilding the shape.
import { labelsSchema, recipeBaseSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
// The full recipe minus the two server-only string-id fields and the
// AI prompt addenda. Same shape the SvelteKit FE consumes.
//
// ADR-0020 (2026-05-23) added three new primitives to recipeBaseSchema —
// `brands?`, `memberRoles[]`, `findingCategorySource`. They flow to the
// FE automatically because we omit() what we want hidden rather than
// rebuilding the shape; new engine fields land on the wire without
// touching this file.
//
// Resolved-only fields (ADR-0027 / #0188): values derived from
// org-config at the route layer. They are NOT recipe primitives — the
// recipe doesn't know about per-org thresholds — but they ride the
// /recipes/me response because the FE's source of truth for the active
// vertical is this single store. Always populated by the route (recipe
// default falls in when the org has no override) so the FE never has
// to fall back to a literal constant.
export const recipeMeResponseSchema = recipeBaseSchema
    .omit({
    aiPromptProfile: true,
    aiPrompts: true,
    pdfTemplate: true,
})
    .extend({
    /** ADR-0027 H-02. Days since last signed acta_visita for a tienda
     *  to count as "al día". Server merges
     *  organizations.config.alDiaThresholdDays with a recipe-default
     *  fallback (30 today). Always present on the response so the FE
     *  consumes `$recipe.alDiaThresholdDays` without nullish fallback. */
    alDiaThresholdDays: z.number().int().positive().max(365),
});
// The resolved-recipe wire shape — server-side merge of the active
// recipe with the org's `organizations.config` overrides
// (labelOverrides, findingCategoriesOverride, membershipRoleLabels).
// Structurally identical to RecipeMeResponse; the difference is
// semantic (the values are already merged, the FE never merges).
//
// Future `GET /recipes/me` endpoint returns this shape; until that
// endpoint lands, callers consume RecipeMeResponse against the raw
// recipe and apply overrides themselves (not recommended, but
// supported during the migration window).
export const resolvedRecipeSchema = recipeMeResponseSchema;
// A pared-down view exposed alongside a public/:token report so the
// share page can render the right labels + severity badges without an
// auth session. Intentionally narrower than recipeMeResponseSchema —
// the public page never needs party roles, signature roles, or default
// areas, so we don't leak them via an unauthenticated endpoint.
export const publicRecipeSchema = z.object({
    slug: z.string(),
    // Derived from labelsSchema (not hand-rolled) so a field this route
    // needs can't silently drift from the engine schema in shape/type —
    // see the reportAttested* keys picked in below for why this matters:
    // a key added to labelsSchema but forgotten here would make the public
    // share page silently fall back to whatever default the component
    // ships, with no error at any layer (ADR-0098 postscript,
    // "reportada" relabeling audit).
    labels: labelsSchema.pick({
        organization: true,
        assetGroup: true,
        asset: true,
        report: true,
        area: true,
        finding: true,
        evidence: true,
        reportAttestedWord: true,
        reportAttestedTitle: true,
        reportAttestedPublicIntegrityLine: true,
        reportAttestedPublicBannerTitle: true,
        reportAttestedPublicFooterFragment: true,
        reportAttestedPublicMeta: true,
    }),
    severities: z.array(z.object({
        key: z.string(),
        label: z.string(),
        rank: z.number().int().nonnegative(),
    })),
    // reportType keys whose areaCatalogMode is "none" (ADR-0056) — lets the
    // public share page's PDF renderer skip area-grouping for an arealess
    // acta without exposing the full reportTypes array (which carries
    // captureMode/milestone/description — more than an unauthenticated
    // page should see).
    arealessReportTypeKeys: z.array(z.string()).default([]),
    // ADR-0058 observationStatuses (broker_postventa), exposed to the
    // public boundary for the postventa share-link provider portal
    // (ADR-0066). Structurally identical to the {key,label,terminal}
    // shape on RecipeMeResponse.observationStatuses — deliberately kept
    // in lockstep so a single visual-resolution function (e.g. the
    // SvelteKit FE's observationStatusVisualFor) works unmodified against
    // either payload. Empty array for verticals that don't use the
    // asset-scoped observation lifecycle, matching the engine default.
    observationStatuses: z
        .array(z.object({
        key: z.string(),
        label: z.string(),
        terminal: z.boolean(),
        // ADR-0073 — derived from the engine's settableBy array (never
        // exposed verbatim: it also names "session_user", an internal
        // detail this boundary doesn't need). Lets the provider portal
        // detect "this is the status MY action already set" without
        // conflating it with terminal — a share-link-settable status is
        // very often non-terminal by design (broker_postventa's
        // atendida_por_proveedor is a claim pending supervisor
        // verification, not a resolution).
        providerSettable: z.boolean(),
    }))
        .default([]),
    // La especialidad que muestran los pills del portal del contratista y
    // del propietario: `findingCategories[].label` por `observation.category`
    // (fundador 2026-10-02; resuelta con `especialidadLabel` de
    // observation-labels.ts). Sólo {key,label} — es vocabulario de la
    // receta, no dato de nadie. Default [] para un core-api anterior al
    // campo: el cliente simplemente no pinta el pill de especialidad.
    findingCategories: z.array(z.object({ key: z.string(), label: z.string() })).default([]),
});
//# sourceMappingURL=recipes.js.map